import React, { useState } from "react";
import { Button, Radio, notification } from "antd";
import PackageChoice from "../../pages/auth/register/components/packageChoice/PackageChoice";
import { isBilledYearly, setBilledYearly } from "../../pages/auth/register/registrationSession";
import { billingApi } from "./billingApi";
import { BillingCode, BillingReason, SUPPORT_EMAIL, describeBillingError } from "./billingCodes";
import { rememberNewSubscription } from "./billingSignIn";
import EnterpriseNeedsField from "./EnterpriseNeedsField";
import EnterpriseOption from "./EnterpriseOption";
import {
   ENTERPRISE_MESSAGE_NOT_SENT,
   EnterpriseRequestSource,
   notifyEnterpriseRequest,
   signedInRequester
} from "./enterpriseRequest";
import {
   ENTERPRISE_PACKET,
   enterpriseTermsToTakeUp,
   hasEnterpriseTermsNow,
   isEnterprisePacket,
   isEnterpriseRequestOpen,
   isOfferWaiting
} from "./enterprise";
import { readDecline, settlePayment } from "./paymentAction";
import PromoCodeField from "./PromoCodeField";
import "../../pages/auth/style.scss";
import "./index.scss";

/**
 * Picking a plan from the billing page, for an organization that has none: one
 * whose sign up never got as far as a plan (offered both kinds of account, and a
 * trial), or one whose subscription has ended (offered every plan of either kind
 * in one row, since the level follows the plan, and no new trial). POST /Payment/subscriptions starts it; the status that comes back is
 * the caller's to act on — a trial lets the organization straight in, a
 * subscription with nothing to charge yet leaves a card to add.
 *
 * `onSubscribed` is also told the promo code the plan was picked with. A plan
 * picked again over one still waiting for a card replaces it, code included,
 * so the caller can hand it back as `initialPromoCode` for the next pick.
 *
 * Enterprise is offered under the plans, by what `billingStatus` says of it
 * (EnterpriseOption.jsx, enterprise.js): its terms are subscribed to like any
 * plan, with no trial; with none agreed yet, picking it only asks for them,
 * and the status that comes back is the organization waiting on our team.
 */
function PlanPicker({
   organizationId,
   organizationLevel,
   offersTrial,
   billingStatus = null,
   initialPromoCode = "",
   onSubscribed
}) {
   const [isYearly, setIsYearly] = useState(isBilledYearly);
   // Enterprise terms just offered are what the organization came back for,
   // so they start out picked. The period is chosen here, with both prices in
   // view: the one a request was sent with is not used, since nothing was
   // priced then (2026-10-22).
   const [plan, setPlan] = useState(() => (isOfferWaiting(billingStatus) ? ENTERPRISE_PACKET : null));
   const [promoCode, setPromoCode] = useState(initialPromoCode);
   // What an Enterprise request tells our team the plan needs.
   const [enterpriseNeeds, setEnterpriseNeeds] = useState("");
   const [isSubscribing, setIsSubscribing] = useState(false);
   const [error, setError] = useState(null);

   const enterpriseTerms = enterpriseTermsToTakeUp(billingStatus);
   const isEnterpriseRequested = isEnterpriseRequestOpen(billingStatus);
   // Enterprise isn't picked again while our team is agreeing its terms, so a
   // pick of it from before it was asked for is let go.
   const picked =
      isEnterprisePacket(plan?.id) && !enterpriseTerms && isEnterpriseRequested ? null : plan;
   const isEnterprisePicked = isEnterprisePacket(picked?.id);
   // Picked with no terms, Enterprise is only asked for: nothing is charged,
   // so there is no promo code to apply. It never has a trial.
   const asksForEnterprise = isEnterprisePicked && !enterpriseTerms;
   const startsTrial = offersTrial && !isEnterprisePicked;

   const handlePeriodChange = (event) => {
      const yearly = event.target.value === "yearly";
      setIsYearly(yearly);
      setBilledYearly(yearly);
   };

   const subscribe = async () => {
      if (!picked || isSubscribing) return;

      setIsSubscribing(true);
      setError(null);

      try {
         // Terms offered since the page read the status would be subscribed to
         // instead of asked for, so they are shown first.
         if (asksForEnterprise && (await hasEnterpriseTermsNow(organizationId))) {
            notification.warning({
               message: "Heads up!",
               description: "Your Enterprise terms are ready. Look them over, then subscribe."
            });
            return;
         }

         // Safe to press twice: a second call returns the same subscription.
         const created = await billingApi.createSubscription(
            {
               organizationId,
               packetId: picked.id,
               isMonthly: !isYearly,
               promoCode: asksForEnterprise ? "" : promoCode
            },
            { rememberStatus: false }
         );
         // The subscription also set the organization's level from the plan —
         // a Customer Feedback plan makes it a business account — so that is
         // read back before its status can let the organization into the app.
         const status = await rememberNewSubscription(organizationId, created);
         const settled = await settlePayment(status, organizationId);

         // Enterprise asked for: our team is sent the request, with what the
         // organization wrote it needs. The request stands either way.
         if (
            asksForEnterprise &&
            settled.status?.reason === BillingReason.ENTERPRISE_OFFER_PENDING &&
            !(await notifyEnterpriseRequest({
               ...signedInRequester(),
               source: EnterpriseRequestSource.BILLING_PAGE,
               needs: enterpriseNeeds
            }))
         ) {
            notification.warning({ message: "Heads up!", description: ENTERPRISE_MESSAGE_NOT_SENT });
         }

         // A refusal is the billing page's to say: the status the bank left puts
         // the fix it asks for in place of the plans.
         onSubscribed(settled.status, { error: settled.error, promoCode });
      } catch (failure) {
         // With no trial left, the card on file is charged at once, and a
         // declined charge is answered 409 with the status it left: the plan is
         // picked and a card is what is asked for now, which the page shows.
         const decline = readDecline(failure);
         if (decline) {
            const status = await rememberNewSubscription(organizationId, decline.status);
            onSubscribed(status, { error: decline.error, promoCode });
            return;
         }

         // A previous annual plan ended unpaid and still owes money: 409 with
         // the status, debt and all. Kept, it holds the organization for that
         // balance, which the page shows in place of the plans.
         if (failure.code === BillingCode.OUTSTANDING_DEBT && failure.status) {
            const status = await rememberNewSubscription(organizationId, failure.status);
            onSubscribed(status, { promoCode });
            return;
         }

         setError({ code: failure.code, text: describeBillingError(failure) });
      } finally {
         setIsSubscribing(false);
      }
   };

   return (
      <div className="package-choice billing-plan-picker">
         <div className="package-choice__billing">
            <Radio.Group onChange={handlePeriodChange} value={isYearly ? "yearly" : "monthly"}>
               <Radio value="yearly">Annual plan</Radio>
               <Radio value="monthly">Monthly plan</Radio>
            </Radio.Group>
         </div>

         <PackageChoice
            isYearly={isYearly}
            selectedPlanId={picked?.id}
            onSelect={setPlan}
            organizationLevel={organizationLevel}
            offersTrial={offersTrial}
            enterpriseOption={
               <EnterpriseOption
                  terms={enterpriseTerms}
                  isRequested={isEnterpriseRequested}
                  isYearly={isYearly}
                  isSelected={isEnterprisePicked}
                  onSelect={() => setPlan(ENTERPRISE_PACKET)}
               />
            }
         />

         {asksForEnterprise && (
            <EnterpriseNeedsField value={enterpriseNeeds} onChange={setEnterpriseNeeds} />
         )}

         {!asksForEnterprise && (
            <PromoCodeField
               promoCode={promoCode}
               onChange={setPromoCode}
               organizationId={organizationId}
               packetId={picked?.id}
               isMonthly={!isYearly}
               trialing={startsTrial}
            />
         )}

         {/* One block, so the picker's gap doesn't push the hint away from the
             button it is about. The button is drawn like the sign up's
             Continue under the same plans (package-choice__continue). */}
         <div>
            {error && (
               <div className="billing-form__message" role="alert">
                  {/* A smaller plan after a cancellation is support's to make:
                      the way to them, not a retry. */}
                  {error.code === BillingCode.DOWNGRADE_REQUIRES_SUPPORT ? (
                     <>
                        Your subscription has been cancelled, so it can't be moved to a smaller plan
                        here. Please{" "}
                        <a href={`mailto:${SUPPORT_EMAIL}`}>contact support at {SUPPORT_EMAIL}</a> to
                        change to a smaller plan, or pick a plan at least the size of the one you had.
                     </>
                  ) : (
                     error.text
                  )}
               </div>
            )}

            <div className="billing-actions billing-actions--center package-choice__continue">
               <Button
                  className="kapSurveyPrimaryButton"
                  onClick={subscribe}
                  loading={isSubscribing}
                  disabled={!picked}
               >
                  {asksForEnterprise
                     ? "Request Enterprise"
                     : startsTrial
                     ? "Start your free trial"
                     : "Subscribe"}
               </Button>
            </div>
            {!picked && <p className="package-choice__continue-hint">Pick a plan to continue.</p>}
         </div>
      </div>
   );
}

export default PlanPicker;
